/**
 * Generated by orval v7.10.0 🍺
 * Do not edit manually.
 * Fintecy Investors API
 * API for managing investors, accounts, and transactions in the Fintecy platform
 * OpenAPI spec version: 1.0.0
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';

import type {
  Account,
  AccountInput,
  AccountListResponse,
  Error,
  GetAllAccountsParams,
} from '.././model';

import { customInstance } from '../../axios-instance';

/**
 * Returns a paginated list of accounts for the authenticated user
 * @summary Get all accounts
 */
export const getAllAccounts = (params?: GetAllAccountsParams, signal?: AbortSignal) => {
  return customInstance<AccountListResponse>({
    url: `/api/accounts`,
    method: 'GET',
    params,
    signal,
  });
};

export const getGetAllAccountsQueryKey = (params?: GetAllAccountsParams) => {
  return [`/api/accounts`, ...(params ? [params] : [])] as const;
};

export const getGetAllAccountsQueryOptions = <
  TData = Awaited<ReturnType<typeof getAllAccounts>>,
  TError = Error,
>(
  params?: GetAllAccountsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllAccounts>>, TError, TData>>;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAllAccountsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAllAccounts>>> = ({ signal }) =>
    getAllAccounts(params, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getAllAccounts>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetAllAccountsQueryResult = NonNullable<Awaited<ReturnType<typeof getAllAccounts>>>;
export type GetAllAccountsQueryError = Error;

export function useGetAllAccounts<
  TData = Awaited<ReturnType<typeof getAllAccounts>>,
  TError = Error,
>(
  params: undefined | GetAllAccountsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllAccounts>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllAccounts>>,
          TError,
          Awaited<ReturnType<typeof getAllAccounts>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAllAccounts<
  TData = Awaited<ReturnType<typeof getAllAccounts>>,
  TError = Error,
>(
  params?: GetAllAccountsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllAccounts>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllAccounts>>,
          TError,
          Awaited<ReturnType<typeof getAllAccounts>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAllAccounts<
  TData = Awaited<ReturnType<typeof getAllAccounts>>,
  TError = Error,
>(
  params?: GetAllAccountsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllAccounts>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get all accounts
 */

export function useGetAllAccounts<
  TData = Awaited<ReturnType<typeof getAllAccounts>>,
  TError = Error,
>(
  params?: GetAllAccountsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllAccounts>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetAllAccountsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Creates a new account for the authenticated user
 * @summary Create a new account
 */
export const createAccount = (accountInput: AccountInput, signal?: AbortSignal) => {
  return customInstance<Account>({
    url: `/api/accounts`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: accountInput,
    signal,
  });
};

export const getCreateAccountMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createAccount>>,
    TError,
    { data: AccountInput },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createAccount>>,
  TError,
  { data: AccountInput },
  TContext
> => {
  const mutationKey = ['createAccount'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createAccount>>,
    { data: AccountInput }
  > = (props) => {
    const { data } = props ?? {};

    return createAccount(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateAccountMutationResult = NonNullable<Awaited<ReturnType<typeof createAccount>>>;
export type CreateAccountMutationBody = AccountInput;
export type CreateAccountMutationError = Error;

/**
 * @summary Create a new account
 */
export const useCreateAccount = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createAccount>>,
      TError,
      { data: AccountInput },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof createAccount>>,
  TError,
  { data: AccountInput },
  TContext
> => {
  const mutationOptions = getCreateAccountMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Returns a single account by its UUID
 * @summary Get account by ID
 */
export const getAccount = (id: string, signal?: AbortSignal) => {
  return customInstance<Account>({ url: `/api/accounts/${id}`, method: 'GET', signal });
};

export const getGetAccountQueryKey = (id: string) => {
  return [`/api/accounts/${id}`] as const;
};

export const getGetAccountQueryOptions = <
  TData = Awaited<ReturnType<typeof getAccount>>,
  TError = Error,
>(
  id: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAccount>>, TError, TData>>;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAccountQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAccount>>> = ({ signal }) =>
    getAccount(id, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getAccount>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetAccountQueryResult = NonNullable<Awaited<ReturnType<typeof getAccount>>>;
export type GetAccountQueryError = Error;

export function useGetAccount<TData = Awaited<ReturnType<typeof getAccount>>, TError = Error>(
  id: string,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAccount>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAccount>>,
          TError,
          Awaited<ReturnType<typeof getAccount>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAccount<TData = Awaited<ReturnType<typeof getAccount>>, TError = Error>(
  id: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAccount>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAccount>>,
          TError,
          Awaited<ReturnType<typeof getAccount>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAccount<TData = Awaited<ReturnType<typeof getAccount>>, TError = Error>(
  id: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAccount>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get account by ID
 */

export function useGetAccount<TData = Awaited<ReturnType<typeof getAccount>>, TError = Error>(
  id: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAccount>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetAccountQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Updates an existing account
 * @summary Update account
 */
export const updateAccount = (id: string, accountInput: AccountInput) => {
  return customInstance<Account>({
    url: `/api/accounts/${id}`,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    data: accountInput,
  });
};

export const getUpdateAccountMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateAccount>>,
    TError,
    { id: string; data: AccountInput },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateAccount>>,
  TError,
  { id: string; data: AccountInput },
  TContext
> => {
  const mutationKey = ['updateAccount'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateAccount>>,
    { id: string; data: AccountInput }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateAccount(id, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateAccountMutationResult = NonNullable<Awaited<ReturnType<typeof updateAccount>>>;
export type UpdateAccountMutationBody = AccountInput;
export type UpdateAccountMutationError = Error;

/**
 * @summary Update account
 */
export const useUpdateAccount = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateAccount>>,
      TError,
      { id: string; data: AccountInput },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof updateAccount>>,
  TError,
  { id: string; data: AccountInput },
  TContext
> => {
  const mutationOptions = getUpdateAccountMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Deletes an existing account (soft delete)
 * @summary Delete account
 */
export const deleteAccount = (id: string) => {
  return customInstance<void>({ url: `/api/accounts/${id}`, method: 'DELETE' });
};

export const getDeleteAccountMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteAccount>>,
    TError,
    { id: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteAccount>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationKey = ['deleteAccount'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteAccount>>, { id: string }> = (
    props
  ) => {
    const { id } = props ?? {};

    return deleteAccount(id);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteAccountMutationResult = NonNullable<Awaited<ReturnType<typeof deleteAccount>>>;

export type DeleteAccountMutationError = Error;

/**
 * @summary Delete account
 */
export const useDeleteAccount = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deleteAccount>>,
      TError,
      { id: string },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof deleteAccount>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationOptions = getDeleteAccountMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Refreshes the balance of a specific account
 * @summary Refresh account balance
 */
export const refreshAccountBalance = (id: string, signal?: AbortSignal) => {
  return customInstance<Account>({ url: `/api/accounts/${id}/refresh`, method: 'POST', signal });
};

export const getRefreshAccountBalanceMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof refreshAccountBalance>>,
    TError,
    { id: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof refreshAccountBalance>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationKey = ['refreshAccountBalance'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof refreshAccountBalance>>,
    { id: string }
  > = (props) => {
    const { id } = props ?? {};

    return refreshAccountBalance(id);
  };

  return { mutationFn, ...mutationOptions };
};

export type RefreshAccountBalanceMutationResult = NonNullable<
  Awaited<ReturnType<typeof refreshAccountBalance>>
>;

export type RefreshAccountBalanceMutationError = Error;

/**
 * @summary Refresh account balance
 */
export const useRefreshAccountBalance = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof refreshAccountBalance>>,
      TError,
      { id: string },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof refreshAccountBalance>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationOptions = getRefreshAccountBalanceMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
